import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  AngebotsId,
  BeginnAblaufGeaendert,
  CheckoutProdukt,
  CheckoutProduktAfterLoadAzam,
  GegeCheckoutProduktPayload,
  GlobalActions,
  NachlassReceived,
  ProduktDeleted,
  RsCheckoutProduktPayload,
  TaasNachlassVorteilReceivedPayload,
  TkvCheckoutProduktPayload,
  TkvV2CheckoutProduktPayload,
  UnfCheckoutProduktPayload,
  VertragslaufzeitChanged,
  VhvCheckoutProduktPayload,
  VhvV2CheckoutProduktPayload,
  VorversicherungVorhandenChangedAction,
  VwgCheckoutProduktPayload
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  areAppTypesEquivalent,
  AUSLANDS_RISIKOORT,
  getSpartenTypeByApp,
  isTierZuJungByToday,
  Vorvertrag,
  Wartezeitverzicht
} from '@ps20-frontend/frontend-models';
import {
  selectUwEntries,
  UnderwriterFragebogenCheckout,
  UwContext,
  UwContextEntry,
  UwSharedDecision,
} from '@ps20-frontend/uw';
import {
  Feature,
  Produktlinienname,
  SofortschutzAvailableItem,
  SpartenType,
  Stockwerk
} from '@ps20/bff-models';
import dayjs from 'dayjs';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { sortByCustomAppOrder } from '@ps20-frontend/reducers';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { v1 as uuidv1 } from 'uuid';
import {
  EinbruchMeldeanlage,
  GegeCheckoutProdukt,
  MechanischeSicherungen,
  Modeaccessoires,
  NichtVerschlusspflichtigeWertsachen,
  Produkt,
  RsBaseProdukt,
  RsCheckoutProdukt,
  TkvCheckoutProdukt,
  TkvV2CheckoutProdukt,
  Tresor,
  TresorEintrag,
  UnfCheckoutProdukt,
  VerschlusspflichtigeWertsachen,
  VhvCheckoutProdukt,
  VhvV2CheckoutProdukt,
  VwgCheckoutProdukt,
  Wertsachen
} from '../model';
import { CheckoutAction, CheckoutActions } from '../checkout.actions';
import { SofortschutzActiveReceived } from '../action-types/sofortschutzActiveReceived';
import { ZielmarktProduktChecked } from '../action-types/zielmarktProduktChecked';
import { ZielmarktBegruendungChanged } from '../action-types/zielmarktBegruendungChanged';
import { orderProductList } from '../selector/checkoutProduktReihenfolge';
import { Vorversicherung } from '../model/vorversicherung';
import {
  EinbruchMeldeanlageChangedPayload,
  FileToDeletePayload,
  HaushaltModalPayload,
  MechanischeSicherungenChangedPayload,
  TresorChangedPayload
} from '../action-types/haushaltModalPayloads';
import {
  WartezeitverzichtReferenzvertragChangedPayload,
  WartezeitverzichtSelectedPayload
} from '../action-types/wartezeitverzicht';
import {
  ElementarfragebogenAnalyseChanged,
  ElementarfragebogenUploadedFilesChange
} from '../action-types/elementarfragebogenAnalyseChanged';

export const MIN_TRESOR_GEWICHT = 300;

const wertsachenWert = (wert?: number): number => +(wert ?? 0);


const verschlusspflichtigeEinzelwertFelder: ReadonlyArray<
  [keyof VerschlusspflichtigeWertsachen, keyof VerschlusspflichtigeWertsachen]
> = [
  ['schmucksachen', 'schmucksachenEinzelwert'],
  ['armbandTaschenuhren', 'armbandTaschenuhrenEinzelwert']
];

const nichtVerschlusspflichtigeEinzelwertFelder: ReadonlyArray<
  [keyof NichtVerschlusspflichtigeWertsachen, keyof NichtVerschlusspflichtigeWertsachen]
> = [['kunstgegenstaende', 'kunstgegenstaendeEinzelwert']];

const begrenzeEinzelwerteAufSumme = (
  wertsachenGruppe: Record<string, unknown>,
  felder: ReadonlyArray<[string, string]>
): void => {
  felder.forEach(([summeFeld, einzelwertFeld]) => {
    const summe = wertsachenWert(wertsachenGruppe[summeFeld] as number);
    if (summe <= 0) {
      wertsachenGruppe[einzelwertFeld] = null;
      return;
    }

    const einzelwert = wertsachenGruppe[einzelwertFeld];
    if (einzelwert != null && wertsachenWert(einzelwert as number) > summe) {
      wertsachenGruppe[einzelwertFeld] = summe;
    }
  });
};

export const initialWertsachen: () => Wertsachen = () => ({
  verschlusspflichtige: {
    bargeld: null,
    schmucksachen: null,
    schmucksachenEinzelwert: null,
    armbandTaschenuhren: null,
    armbandTaschenuhrenEinzelwert: null,
    wertpapiere: null,
    edelmetalle: null,
    summe: 0,
    tresoreVorhanden: null,
    modified: false
  },
  nichtVerschlusspflichtige: {
    antiquitaten: null,
    kunstgegenstaende: null,
    kunstgegenstaendeEinzelwert: null,
    silbersachen: null,
    summe: 0,
    einbruchmeldeanlageVorhanden: null,
    modified: false
  },
  modeaccessoires: {
    taschen: null,
    schuhpaare: null,
    guertel: null,
    sonstige: null,
    summe: 0,
    modified: false
  },
  summe: 0
});

export const INITIAL_EMA: EinbruchMeldeanlage = {
  status: 'OPEN',
  saved: false,
  klasse: null,
  vds2170: null,
  vds2529: null,
  meldeanlageBeschreibung: null,
  files: []
};

export const INITIAL_TRESOR_EINTRAG: TresorEintrag = {
  sicherheitsstufe: null,
  verbauung: null,
  gewicht: null,
  ueberwachung: null,
  files: [],
  tresorId: uuidv1()
};

export const INITIAL_TRESOR: Tresor = {
  status: 'OPEN',
  saved: false,
  eintraege: [
    {
      ...INITIAL_TRESOR_EINTRAG,
      tresorId: uuidv1()
    }
  ]
};

export const INITIAL_MECHANISCHE_SICHERUNGEN: MechanischeSicherungen = {
  status: 'OPEN',
  saved: false,
  aussentueren: {
    schliesszylinder: {
      vorhanden: null,
      nachruestungDatum: null
    },
    querriegelschloss: {
      vorhanden: null,
      nachruestungDatum: null
    },
    verglasungen: {
      vorhanden: null,
      gitter: {
        vorhanden: null,
        nachruestungDatum: null
      },
      eh1Verglasung: {
        vorhanden: null,
        nachruestungDatum: null
      },
      andereSicherung: {
        vorhanden: null,
        nachruestungDatum: null,
        nachruestungBeschreibung: null
      }
    }
  },
  balkontueren: {
    zusatzschloss: {
      vorhanden: null,
      nachruestungDatum: null
    },
    zweifachverriegelung: {
      vorhanden: null,
      nachruestungDatum: null
    },
    andereSicherung: {
      vorhanden: null,
      nachruestungDatum: null,
      nachruestungBeschreibung: null
    }
  },
  fenster: {
    zusatzschloss: {
      vorhanden: null,
      nachruestungDatum: null
    },
    zweifachverriegelung: {
      vorhanden: null,
      nachruestungDatum: null
    },
    gitter: {
      vorhanden: null,
      nachruestungDatum: null
    },
    andereSicherung: {
      vorhanden: null,
      nachruestungDatum: null,
      nachruestungBeschreibung: null
    }
  },
  kellerfenster: {
    stahllochblende: {
      vorhanden: null,
      nachruestungDatum: null
    },
    gitter: {
      vorhanden: null,
      nachruestungDatum: null
    },
    lichtschachtrost: {
      vorhanden: null,
      nachruestungDatum: null
    }
  },
  weitereAbsicherungen: null,
  files: []
};

const EMPTY_UW_SHARED_DECISION: UwSharedDecision = {
  measures: [],
  documentation: '',
  underwriterName: '',
  underwriterBensl: '',
  risikoAddress: '',
  produktName: '',
};

function handleUpdateBeginnAblaufOfProdukte(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: BeginnAblaufGeaendert[] = action.payload;
  return sectionState.map(produkt => {
    const matchingUpdate = actionPayload.find(
      ({ appId, angebotsId }) => produkt.appId === appId && produkt.angebotsId === angebotsId,
    );
    if (matchingUpdate) {
      return {
        ...produkt,
        beginn: matchingUpdate.beginn,
        ablauf: matchingUpdate.ablauf,
      };
    }
    return produkt;
  });
}

function handleUwResubmissionFulfilledAngeboteCollected(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const angebotsIds: Array<string> = action.payload?.angebotsIds || [];
  const newSectionState = cloneDeep(sectionState);

  angebotsIds.forEach(angebotsId => {
    const produkt = newSectionState.find(p => p.angebotsId === angebotsId) as VwgCheckoutProdukt;
    if (produkt) {
      produkt.uw = undefined;
      produkt.zuschlagRisiko = undefined;
    }
  });

  return newSectionState;
}

function handleElementarfragebogenProvided(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newSectionState = cloneDeep(sectionState);
  const actionPayload: UnderwriterFragebogenCheckout = action.payload;

  actionPayload.products.forEach(entry => {
    const produkt = newSectionState.find(
      p => p.appId === entry.appId && p.angebotsId === entry.angebotsId,
    ) as VwgCheckoutProdukt;

    if (!produkt) {
      return;
    }

    const decidedEntries = entry.uw.entries.filter(
      context =>
        context.willUnderwrite !== null &&
        (context.isRequired || context.willUnderwrite === false),
    );
    const noLongerRequiredContextKeys = new Set(
      entry.uw.entries.filter(context => !context.isRequired).map(context => context.contextKey),
    );
    const existingEntries = produkt.uw?.entries ?? [];
    const hasStaleEntryToDrop = existingEntries.some(existing =>
      noLongerRequiredContextKeys.has(existing.contextKey),
    );
    if (decidedEntries.length === 0 && !hasStaleEntryToDrop) {
      return;
    }

    const decidedContextKeys = new Set(decidedEntries.map(context => context.contextKey));
    const preservedEntries = existingEntries.filter(
      existing =>
        !decidedContextKeys.has(existing.contextKey) &&
        !noLongerRequiredContextKeys.has(existing.contextKey),
    );

    produkt.uw = {
      shared: entry.uw.shared,
      entries: [...preservedEntries, ...decidedEntries],
    };
  });

  return newSectionState;
}

function handleElementarfragebogenAnalyseChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newSectionState = cloneDeep(sectionState);
  const actionPayload: ElementarfragebogenAnalyseChanged = action.payload;

  const matchingProdukte = newSectionState.filter(
    produkt => areAppTypesEquivalent(produkt.appId, App.VWG) && produkt.angebotsId === actionPayload.angebotsId,
  ) as VwgCheckoutProdukt[];

  if (matchingProdukte.length !== 1) {
    return newSectionState;
  }

  const produkt = matchingProdukte[0];
  const otherEntries = (produkt.uw?.entries ?? []).filter(
    entry => entry.contextKey !== UwContext.NATURAL_HAZARDS,
  );

  if (actionPayload.changed) {
    const naturalHazardsEntry: UwContextEntry = {
      contextKey: UwContext.NATURAL_HAZARDS,
      isRequired: true,
      willUnderwrite: null,
      files: [],
      texts: {},
      reason: '',
    };
    produkt.uw = {
      shared: produkt.uw?.shared ?? EMPTY_UW_SHARED_DECISION,
      entries: [...otherEntries, naturalHazardsEntry],
    };
  } else {
    produkt.uw = otherEntries.length > 0
      ? { shared: produkt.uw.shared, entries: otherEntries }
      : undefined;
  }
  return newSectionState;
}

function handleElementarfragebogenUploadedFilesChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newSectionState = cloneDeep(sectionState);
  const actionPayload: ElementarfragebogenUploadedFilesChange = action.payload;

  const produkt = newSectionState.find(
    produkt => produkt.angebotsId === actionPayload.angebotsId,
  ) as VwgCheckoutProdukt;
  const naturalHazardsEntry = selectUwEntries(produkt?.uw, UwContext.NATURAL_HAZARDS)[0];
  if (naturalHazardsEntry) {
    naturalHazardsEntry.files = actionPayload.uploadedfiles;
  }
  return newSectionState;
}

function handleVertragslaufzeitChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newSectionState: Produkt[] = cloneDeep(sectionState);
  newSectionState.forEach(produkt => {
    const actionPayload: VertragslaufzeitChanged = action.payload;
    const { appId, angebotsId } = actionPayload;
    if (produkt.appId === appId && produkt.angebotsId === angebotsId) {
      produkt.laufzeit = action.payload.vertragslaufzeit;
    }
  });
  return newSectionState;
}

function handleProduktDeleted(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const produktDeletedPayload: ProduktDeleted = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);
  return newState.filter(
    element =>
      !produktDeletedPayload.angebotsIds.some(
        angebotsId =>
          angebotsId.appId === element.appId && angebotsId.angebotsId === element.angebotsId,
      ),
  );
}

function updateMatchingProdukt(
  newState: Array<Produkt>,
  produkt: CheckoutProdukt,
  state: IAppState,
): void {
  const matchingProdukt: Produkt = findMatchingProdukt(newState, produkt);
  const previousProduktlinie = matchingProdukt.produktlinie;
  matchingProdukt.name = produkt.uebersichtTitle;
  matchingProdukt.produktlinie = produkt.produktlinie;
  matchingProdukt.laufzeit = produkt.laufzeit;
  matchingProdukt.angebotsdatum = produkt.angebotsdatum;
  matchingProdukt.versichertesRisiko = getVersichertesRisiko(produkt);
  matchingProdukt.vorvertragSofortschutz = produkt.vorvertragSofortschutz;
  matchingProdukt.vorvertragsnummer = produkt.vorvertragsnummer;
  matchingProdukt.beginn = produkt.beginndatum ? produkt.beginndatum : matchingProdukt.beginn;
  matchingProdukt.beibehalten = produkt.beibehalten;
  matchingProdukt.extremwetterschutzBeratunghinweis =
    produkt.extremwetterschutzBeratunghinweis;
  matchingProdukt.isImmobilieImAusland = !!produkt.isImmobilieImAusland;
  matchingProdukt.premiumAndExtremwetterDisabled = produkt.premiumAndExtremwetterDisabled;
  matchingProdukt.showEleUpload = produkt.showEleUpload;
  setSpartenDatenForCheckout(produkt, matchingProdukt, previousProduktlinie);

  if (matchingProdukt.appId === App.TKV && matchingProdukt.sofortschutzAvailable) {
    checkTkvSofortschutz(matchingProdukt as TkvCheckoutProdukt, state);
  }
  if (matchingProdukt.appId === App.TKV_V2 && matchingProdukt.sofortschutzAvailable) {
    checkTkvV2Sofortschutz(matchingProdukt as TkvV2CheckoutProdukt, state);
  }
}

function handleProduktAction(
  sectionState: Array<Produkt>,
  action: AnyAction,
  state: IAppState,
): Array<Produkt> {
  const produkt: CheckoutProdukt = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);

  if (shouldAddNewProdukt(newState, produkt)) {
    const produktToAdd: Produkt = {
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
      angebotsdatum: produkt.angebotsdatum,
      produktangebotId: Date.now().toString(),
      name: produkt.uebersichtTitle,
      produktlinie: produkt.produktlinie,
      laufzeit: produkt.laufzeit,
      versichertesRisiko: getVersichertesRisiko(produkt),
      beginn: calculateVBeginnDatum(produkt),
      ablauf: dayjs().add(1, 'd').add(produkt.laufzeit, 'year').format(DATEFORMAT_LOCAL_STATE),
      sofortschutzAvailable: false,
      sofortschutzActive: false,
      beginndatumChangeable: true,
      sofortschutzAblauf: '',
      vorvertragsnummer: produkt.vorvertragsnummer,
      abweichungVomZielmarkt: false,
      zielmarktBegruendung: '',
      vorvertragSofortschutz: produkt.vorvertragSofortschutz,
      beibehalten: produkt.beibehalten ? produkt.beibehalten : false,
      wirksamkeitsbeginn: produkt.wirksamkeitsbeginn,
      shouldDisableBeginndatum: produkt.shouldDisableBeginndatum && produkt.beibehalten,
      countersIncreases: produkt.countersIncreases,
      extremwetterschutzBeratunghinweis: produkt.extremwetterschutzBeratunghinweis,
      premiumAndExtremwetterDisabled: produkt.premiumAndExtremwetterDisabled,
      isImmobilieImAusland: produkt.isImmobilieImAusland,
      beitragsfreiBeibehalten: produkt.beitragsfreiBeibehalten && produkt.beibehalten,
      showEleUpload: produkt.showEleUpload,
    };
    setSpartenDatenForCheckout(produkt, produktToAdd);
    newState.push(produktToAdd);
  } else {
    updateMatchingProdukt(newState, produkt, state);
  }
  return newState;
}

function handleUpdateCheckoutAfterLoadAzam(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const { appId, angebotsId, versichertesRisiko }: CheckoutProduktAfterLoadAzam =
    action.payload;
  return sectionState.map((elem: Produkt): Produkt => {
    if (elem.appId !== appId || elem.angebotsId !== angebotsId) {
      return elem;
    }
    return {
      ...elem,
      versichertesRisiko,
    };
  });
}

function handleWertsachenChanged(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const actionPayload: HausHaltWertSachenChanged = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);
  return newState.map(produkt => {
    if (
      produkt.appId === actionPayload.appId &&
      produkt.angebotsId === actionPayload.angebotsId
    ) {
      const updatedProdukt = {
        ...produkt,
        hasWertsachen: actionPayload.hasWertsachen,
      };
      if ('wertsachen' in updatedProdukt) {
        updatedProdukt['wertsachen'] = initialWertsachen();
      }
      return updatedProdukt;
    }
    return produkt;
  });
}

function handleSofortschutzAvailable(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: SofortschutzAvailableItem[] = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);
  return newState.map(value => {
    const produktSpartenType = getSpartenTypeByApp(value.appId);
    const sofortschutzAvailableItem = actionPayload.find(item => {
      const spartenTypeMatches =
        item.spartenType === produktSpartenType ||
        (value.appId === App.TKV_V2 && item.spartenType === SpartenType.TIERKRANKEN);
      return spartenTypeMatches && (!item.angebotsId || item.angebotsId === value.angebotsId);
    });
    value.sofortschutzAvailable = sofortschutzAvailableItem
      ? sofortschutzAvailableItem.isAvailable
      : false;
    return value;
  });
}

function handleVorversicherungVorhandenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const vorversicherungVorhandenChangedAction: VorversicherungVorhandenChangedAction = action;
  const payload = vorversicherungVorhandenChangedAction.payload;
  return sectionState.map(produkt => {
    if (produkt.appId === payload.appId && produkt.angebotsId === payload.angebotsId) {
      return {
        ...produkt,
        sofortschutzAblauf: '',
        sofortschutzActive: false,
        beginndatumChangeable: true,
      };
    }
    return produkt;
  });
}

function handleUpdateSofortschutzEndDate(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const vorvertraege: Vorvertrag[] = action.payload;
  return sectionState.map(produkt => {
    const matchingVorvertrag = vorvertraege.find(vorvertrag =>
      produkt.vorvertragsnummer?.some(
        produktVorvertrag => vorvertrag.vertragsnummer === produktVorvertrag.vertragsnummer,
      ),
    );
    if (matchingVorvertrag) {
      return {
        ...produkt,
        vorvertragSofortschutzAblaufDatum: matchingVorvertrag.vorvertragSofortschutzAblaufDatum,
      };
    }
    return produkt;
  });
}

function handleSofortschutzActiveReceived(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: SofortschutzActiveReceived = action.payload;
  return sectionState.map(produkt => {
    if (
      produkt.appId === actionPayload.appId &&
      produkt.angebotsId === actionPayload.angebotsId
    ) {
      return {
        ...produkt,
        sofortschutzAblauf: actionPayload.sofortschutzEndeDatum,
        sofortschutzActive: actionPayload.sofortschutzActive,
        beginndatumChangeable: actionPayload.beginndatumChangeable,
      };
    } else {
      return produkt;
    }
  });
}

function handleAmtlichesKennzeichenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newState: Array<Produkt> = cloneDeep(sectionState);
  const rsProdukt = newState.find(
    stateProdukt => stateProdukt.appId === App.RS,
  ) as RsCheckoutProdukt;
  rsProdukt.amtlichesKennzeichen = action.payload === '' ? null : action.payload;
  return newState;
}

function handleWartezeitverzichtSelected(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newState: Array<Produkt> = cloneDeep(sectionState);
  const payload: WartezeitverzichtSelectedPayload = action.payload;
  const rsProdukt = newState.find(
    stateProdukt => stateProdukt.appId === payload.appId,
  ) as RsBaseProdukt;
  rsProdukt.wartezeitverzicht = {
    ...rsProdukt.wartezeitverzicht,
    wartezeitverzichtSelected: payload.value,
  } as Wartezeitverzicht;
  return newState;
}

function handleWartezeitverzichtReferenzvertragChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const newState: Array<Produkt> = cloneDeep(sectionState);
  const payload: WartezeitverzichtReferenzvertragChangedPayload = action.payload;
  const rsProdukt = newState.find(
    stateProdukt => stateProdukt.appId === payload.appId,
  ) as RsBaseProdukt;
  rsProdukt.wartezeitverzicht = {
    ...rsProdukt.wartezeitverzicht,
    referenzvertrag: payload.value,
  } as Wartezeitverzicht;
  return newState;
}

function handleZielmarktProduktChecked(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: ZielmarktProduktChecked = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);

  newState.forEach(produkt => {
    if (
      produkt.appId === actionPayload.appId &&
      produkt.angebotsId === actionPayload.angebotsId
    ) {
      produkt.abweichungVomZielmarkt = actionPayload.zielmarktChecked;
      produkt.zielmarktBegruendung = '';
    }
  });

  return newState;
}

function handleZielmarktBegruendungChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: ZielmarktBegruendungChanged = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);

  newState.forEach(produkt => {
    if (
      produkt.appId === actionPayload.produktId &&
      produkt.angebotsId === actionPayload.angebotsId
    ) {
      produkt.zielmarktBegruendung = actionPayload.begruendung;
    }
  });

  return newState;
}

function handleZielmarktChanged(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const actionPayload = action.payload;
  const newState: Array<Produkt> = cloneDeep(sectionState);

  newState.forEach(produkt => {
    if (actionPayload.verkaufInnerhalbZielmarkt === true) {
      produkt.abweichungVomZielmarkt = false;
      produkt.zielmarktBegruendung = '';
    } else if (newState.length === 1) {
      produkt.abweichungVomZielmarkt = true;
    } else {
      produkt.zielmarktBegruendung = '';
    }
  });

  return newState;
}

function handleSortProdukte(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const angeboteInWarenkorb: Array<AngebotsId> = action.payload;
  const unfallProdukte = sectionState.filter(produkt => produkt.appId === App.UNF);
  unfallProdukte.forEach(unfProdukt =>
    angeboteInWarenkorb.push({
      appId: unfProdukt.appId,
      angebotsId: unfProdukt.angebotsId,
    }),
  );

  if (angeboteInWarenkorb?.length > 0) {
    const newState: Array<Produkt> = cloneDeep(sectionState);
    const sortedProdukte: Array<Produkt> = angeboteInWarenkorb.map(warenkorbAngebotsId => {
      return newState.find(
        produkt =>
          produkt.appId === warenkorbAngebotsId.appId &&
          produkt.angebotsId === warenkorbAngebotsId.angebotsId,
      );
    });
    if (sortedProdukte && sortedProdukte.length === sectionState.length) {
      return sortByCustomAppOrder(orderProductList, sortedProdukte);
    }
  }
  return sectionState;
}

function handleVhvWertsachenSummenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
  state: IAppState,
): Array<Produkt> {
  const actionPayload: {
    angebotsId: string;
    wertsachen: Wertsachen;
  } = (
    action as CheckoutAction<{
      angebotsId: string;
      wertsachen: Wertsachen;
    }>
  ).payload;
  const isVhv2xActive = Boolean(selectFeatureToggle(state, Feature.VHV2X));
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;

      const verschlusspflichtige = { ...actionPayload.wertsachen.verschlusspflichtige };
      const nichtVerschlusspflichtige = {
        ...actionPayload.wertsachen.nichtVerschlusspflichtige,
      };

      begrenzeEinzelwerteAufSumme(verschlusspflichtige, verschlusspflichtigeEinzelwertFelder);
      begrenzeEinzelwerteAufSumme(
        nichtVerschlusspflichtige,
        nichtVerschlusspflichtigeEinzelwertFelder,
      );

      const wertpapiereSumme = isVhv2xActive
        ? 0
        : wertsachenWert(verschlusspflichtige.wertpapiere);

      verschlusspflichtige.summe =
        wertsachenWert(verschlusspflichtige.edelmetalle) +
        wertsachenWert(verschlusspflichtige.bargeld) +
        wertsachenWert(verschlusspflichtige.schmucksachen) +
        wertsachenWert(verschlusspflichtige.armbandTaschenuhren) +
        wertpapiereSumme;

      nichtVerschlusspflichtige.summe =
        wertsachenWert(nichtVerschlusspflichtige.silbersachen) +
        wertsachenWert(nichtVerschlusspflichtige.antiquitaten) +
        wertsachenWert(nichtVerschlusspflichtige.kunstgegenstaende);

      return {
        ...produktVhv,
        wertsachen: {
          ...actionPayload.wertsachen,
          verschlusspflichtige,
          nichtVerschlusspflichtige,
          summe: nichtVerschlusspflichtige.summe + verschlusspflichtige.summe,
        },
      };
    }
    return produkt;
  });
}

function handleVhvModeaccessoiresChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: { angebotsId: string; modeaccessoires: Modeaccessoires } = (
    action as CheckoutAction<{ angebotsId: string; modeaccessoires: Modeaccessoires }>
  ).payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;

      const modeaccessoires = { ...actionPayload.modeaccessoires };
      modeaccessoires.summe =
        wertsachenWert(modeaccessoires.taschen) +
        wertsachenWert(modeaccessoires.schuhpaare) +
        wertsachenWert(modeaccessoires.guertel) +
        wertsachenWert(modeaccessoires.sonstige);

      return {
        ...produktVhv,
        wertsachen: {
          ...produktVhv.wertsachen,
          modeaccessoires,
        },
      };
    }
    return produkt;
  });
}

function handleEmaVorhandenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: { angebotsId: string; emaVorhanden: boolean } = (
    action as CheckoutAction<{ angebotsId: string; emaVorhanden: boolean }>
  ).payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.wertsachen = { ...produktVhv.wertsachen };
      produktVhv.wertsachen.nichtVerschlusspflichtige.einbruchmeldeanlageVorhanden =
        actionPayload.emaVorhanden;
    }
    return produkt;
  });
}

function handleTresoreVorhandenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: { angebotsId: string; tresoreVorhanden: boolean } = (
    action as CheckoutAction<{ angebotsId: string; tresoreVorhanden: boolean }>
  ).payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.wertsachen = { ...produktVhv.wertsachen };
      produktVhv.wertsachen.verschlusspflichtige.tresoreVorhanden =
        actionPayload.tresoreVorhanden;
    }
    return produkt;
  });
}

function handleEinbruchmeldeanlageModalOpened(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: HaushaltModalPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      (produkt as VhvCheckoutProdukt).einbruchMeldeanlage.status = 'INCOMPLETE';
    }
    return produkt;
  });
}

function handleWertbehaeltnisseModalOpened(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: HaushaltModalPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      (produkt as VhvCheckoutProdukt).tresor.status = 'INCOMPLETE';
    }
    return produkt;
  });
}

function handleMechanischeSicherungenModalOpened(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: HaushaltModalPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      (produkt as VhvCheckoutProdukt).mechanischeSicherungen.status = 'INCOMPLETE';
    }
    return produkt;
  });
}

function handleEmaChanged(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const actionPayload: EinbruchMeldeanlageChangedPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.einbruchMeldeanlage = actionPayload.einbruchMeldeanlage;
      produktVhv.einbruchMeldeanlage.status = actionPayload.isFormValid
        ? 'COMPLETE'
        : 'INCOMPLETE';
      produktVhv.einbruchMeldeanlage.saved = true;
    }
    return produkt;
  });
}

function handleTresorChanged(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const actionPayload: TresorChangedPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.tresor.eintraege = actionPayload.tresorEintraege;
      produktVhv.tresor.status = actionPayload.isFormValid ? 'COMPLETE' : 'INCOMPLETE';
      produktVhv.tresor.saved = true;
    }
    return produkt;
  });
}

function handleMechanischeSicherungenChanged(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const actionPayload: MechanischeSicherungenChangedPayload = action.payload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && produkt.angebotsId === actionPayload.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.mechanischeSicherungen = actionPayload.mechanischeSicherungen;
      produktVhv.mechanischeSicherungen.status = actionPayload.isFormValid
        ? 'COMPLETE'
        : 'INCOMPLETE';
      produktVhv.mechanischeSicherungen.saved = true;
    }
    return produkt;
  });
}

function handleFileDeleted(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const payload = action.payload as FileToDeletePayload;

  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && payload.angebotsId === produkt.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      if (produktVhv.mechanischeSicherungen) {
        produktVhv.mechanischeSicherungen.files =
          produktVhv.mechanischeSicherungen.files?.filter(
            file => file.uploadId !== payload.documentId,
          );
      }
      if (produktVhv.einbruchMeldeanlage) {
        produktVhv.einbruchMeldeanlage.files = produktVhv.einbruchMeldeanlage.files?.filter(
          file => file.uploadId !== payload.documentId,
        );
      }
      if (produktVhv.tresor) {
        produktVhv.tresor.eintraege?.forEach(
          tresor =>
            (tresor.files = tresor.files.filter(file => file.uploadId !== payload.documentId)),
        );
      }
    }
    return produkt;
  });
}

function handleRemoveNotExistingFiles(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const payload = action.payload as Array<string>;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV)) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      if (produktVhv.mechanischeSicherungen) {
        produktVhv.mechanischeSicherungen.files =
          produktVhv.mechanischeSicherungen.files?.filter(file =>
            payload.includes(file.uploadId),
          );
      }
      if (produktVhv.einbruchMeldeanlage) {
        produktVhv.einbruchMeldeanlage.files = produktVhv.einbruchMeldeanlage.files?.filter(
          file => payload.includes(file.uploadId),
        );
      }
      if (produktVhv.tresor) {
        produktVhv.tresor.eintraege?.forEach(
          tresor =>
            (tresor.files = tresor.files.filter(file => payload.includes(file.uploadId))),
        );
      }
    } else if (areAppTypesEquivalent(produkt.appId, App.VWG)) {
      const produktVwg = produkt as VwgCheckoutProdukt;
      produktVwg.uw?.entries.forEach(entry => {
        entry.files = entry.files.filter(file => payload.includes(file.uploadId));
      });
    }
    return produkt;
  });
}

function handleMarkFileToDelete(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const payload = action.payload as FileToDeletePayload;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && payload.angebotsId === produkt.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.filesToDelete.push({
        uploadId: payload.documentId,
        componentId: payload.fileUploaderId,
      });
    }
    return produkt;
  });
}

function handleCleanupFilesToDelete(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const angebotsId = action.payload.angebotsId;
  return sectionState.map(produkt => {
    if (areAppTypesEquivalent(produkt.appId, App.VHV) && angebotsId === produkt.angebotsId) {
      const produktVhv = produkt as VhvCheckoutProdukt;
      produktVhv.filesToDelete = [];
    }
    return produkt;
  });
}

function handleNachlassReceived(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const nachlassPayload: NachlassReceived = action.payload;
  const sectionStateCopy = cloneDeep(sectionState);
  const nachlassInt = nachlassPayload.aktionsNachlass;
  sectionStateCopy.forEach(produkt => {
    if (
      nachlassInt > 0 &&
      produkt.appId === nachlassPayload.appId &&
      produkt.angebotsId === nachlassPayload.angebotsId
    ) {
      produkt.kooperation = nachlassPayload.code;
      return produkt;
    }
  });

  return sectionStateCopy;
}

function handleTaasNachlassRabattReceived(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const payload = action.payload as TaasNachlassVorteilReceivedPayload;
  const taasNachlassVorteil = payload.taasNachlassVorteil;
  const salesProduktVariants = payload.salesProduktVariants;
  sectionState.forEach(produkt => {
    if (
      payload.appId === produkt.appId &&
      payload.angebotsId === produkt.angebotsId &&
      taasNachlassVorteil
    ) {
      produkt.vorteilsrabattBelastungInput = {
        taasNachlassVorteil,
        cpSternSum: salesProduktVariants[0].cpSternSum,
        jahresNettoBausteineSum: salesProduktVariants[0].jahresNettoBausteineSum,
      };
      return produkt;
    }
  });
  return sectionState;
}

function handleZuschlagrisikoReceived(
  sectionState: Array<Produkt>,
  action: AnyAction,
): Array<Produkt> {
  const payload = action.payload;
  sectionState.forEach(produkt => {
    if (payload.appId === produkt.appId && payload.angebotsId === produkt.angebotsId) {
      produkt.zuschlagRisiko = payload.zuschlagRisiko;
      return produkt;
    }
  });
  return sectionState;
}

function handleZuersZoneReceived(sectionState: Array<Produkt>, action: AnyAction): Array<Produkt> {
  const payload = action.payload;
  return sectionState.map(produkt => {
    if (payload.appId === produkt.appId && payload.angebotsId === produkt.angebotsId) {
      return {
        ...produkt,
        zuersZone: payload.zuersZone,
      } as VhvV2CheckoutProdukt;
    }
    return produkt;
  });
}

export const produkteReducer = function(
  sectionState: Array<Produkt> = [],
  action: AnyAction,
  state: IAppState
): Array<Produkt> {
  switch (action.type) {
    case GlobalActions.UPDATE_BEGINN_ABLAUF_OF_PRODUKTE:
      return handleUpdateBeginnAblaufOfProdukte(sectionState, action);
    case GlobalActions.UW_RESUBMISSION_FULFILLED_ANGEBOTE_COLLECTED:
      return handleUwResubmissionFulfilledAngeboteCollected(sectionState, action);
    case GlobalActions.ELEMENTARFRAGEBOGEN_PROVIDED:
      return handleElementarfragebogenProvided(sectionState, action);
    case CheckoutActions.ELEMENTARFRAGEBOGEN_ANALYSE_CHANGED:
      return handleElementarfragebogenAnalyseChanged(sectionState, action);
    case CheckoutActions.ELEMENTARFRAGEBOGEN_UPLOADED_FILES_CHANGED:
      return handleElementarfragebogenUploadedFilesChanged(sectionState, action);
    case GlobalActions.VERTRAGSLAUFZEIT_CHANGED:
      return handleVertragslaufzeitChanged(sectionState, action);
    case GlobalActions.PRODUKT_DELETED:
      return handleProduktDeleted(sectionState, action);
    case GlobalActions.PRODUKT_ACTION:
      return handleProduktAction(sectionState, action, state);
    case GlobalActions.UPDATE_CHECKOUT_AFTER_LOAD_AZAM:
      return handleUpdateCheckoutAfterLoadAzam(sectionState, action);
    case CheckoutActions.WERTSACHEN_CHANGED:
      return handleWertsachenChanged(sectionState, action);
    case GlobalActions.SOFORTSCHUTZ_AVAILABLE:
      return handleSofortschutzAvailable(sectionState, action);
    case GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED:
      return handleVorversicherungVorhandenChanged(sectionState, action);
    case GlobalActions.UPDATE_SOFORTSCHUTZ_END_DATE:
      return handleUpdateSofortschutzEndDate(sectionState, action);
    case CheckoutActions.SOFORTSCHUTZ_ACTIVE_RECEIVED:
      return handleSofortschutzActiveReceived(sectionState, action);
    case CheckoutActions.AMTLICHES_KENNZEICHEN_CHANGED:
      return handleAmtlichesKennzeichenChanged(sectionState, action);
    case CheckoutActions.WARTEZEITVERZICHT_SELECTED:
      return handleWartezeitverzichtSelected(sectionState, action);
    case CheckoutActions.WARTEZEITVERZICHT_REFERENZVERTRAG_CHANGED:
      return handleWartezeitverzichtReferenzvertragChanged(sectionState, action);
    case CheckoutActions.ZIELMARKT_PRODUKT_CHECKED:
      return handleZielmarktProduktChecked(sectionState, action);
    case CheckoutActions.ZIELMARKT_BEGRUENDUNG_CHANGED:
      return handleZielmarktBegruendungChanged(sectionState, action);
    case CheckoutActions.ZIELMARKT_CHANGED:
      return handleZielmarktChanged(sectionState, action);
    case CheckoutActions.SORT_PRODUKTE:
      return handleSortProdukte(sectionState, action);
    case CheckoutActions.VHV_WERTSACHEN_SUMMEN_CHANGED:
      return handleVhvWertsachenSummenChanged(sectionState, action, state);
    case CheckoutActions.VHV_MODEACCESSOIRES_CHANGED:
      return handleVhvModeaccessoiresChanged(sectionState, action);
    case CheckoutActions.EMA_VORHANDEN_CHANGED:
      return handleEmaVorhandenChanged(sectionState, action);
    case CheckoutActions.TRESORE_VORHANDEN_CHANGED:
      return handleTresoreVorhandenChanged(sectionState, action);
    case CheckoutActions.EINBRUCHMELDEANLAGE_MODAL_OPENED:
      return handleEinbruchmeldeanlageModalOpened(sectionState, action);
    case CheckoutActions.WERTBEHAELTNISSE_MODAL_OPENED:
      return handleWertbehaeltnisseModalOpened(sectionState, action);
    case CheckoutActions.MECHANISCHE_SICHERUNGEN_MODAL_OPENED:
      return handleMechanischeSicherungenModalOpened(sectionState, action);
    case CheckoutActions.EMA_CHANGED:
      return handleEmaChanged(sectionState, action);
    case CheckoutActions.TRESOR_CHANGED:
      return handleTresorChanged(sectionState, action);
    case CheckoutActions.MECHANISCHE_SICHERUNGEN_CHANGED:
      return handleMechanischeSicherungenChanged(sectionState, action);
    case CheckoutActions.FILE_DELETED:
      return handleFileDeleted(sectionState, action);
    case CheckoutActions.REMOVE_NOT_EXISTING_FILES:
      return handleRemoveNotExistingFiles(sectionState, action);
    case CheckoutActions.MARK_FILE_TO_DELETE:
      return handleMarkFileToDelete(sectionState, action);
    case CheckoutActions.CLEANUP_FILES_TO_DELETE:
      return handleCleanupFilesToDelete(sectionState, action);
    case GlobalActions.NACHLASS_RECEIVED:
      return handleNachlassReceived(sectionState, action);
    case GlobalActions.TAAS_NACHLASS_RABATT_RECEIVED:
      return handleTaasNachlassRabattReceived(sectionState, action);
    case GlobalActions.ZUSCHLAGRISIKO_RECEIVED:
      return handleZuschlagrisikoReceived(sectionState, action);
    case GlobalActions.ZUERS_ZONE_RECEIVED:
      return handleZuersZoneReceived(sectionState, action);
    default:
      return sectionState;
  }
};

function setSpartenDatenForCheckout(
  produktFromPayload: CheckoutProdukt,
  produktToAdd: Produkt,
  previousProduktlinie?: Produktlinienname
): void {
  produktToAdd.angebotsdatum = produktFromPayload.angebotsdatum;
  switch (produktFromPayload.appId) {
    case App.VHV: {
      setVHVDaten(
        produktFromPayload as VhvCheckoutProduktPayload,
        produktToAdd as VhvCheckoutProdukt,
        previousProduktlinie
      );
      break;
    }
    case App.VHV_V2: {
      setVhvV2Daten(
        produktFromPayload as VhvV2CheckoutProduktPayload,
        produktToAdd as VhvV2CheckoutProdukt,
        previousProduktlinie
      );
      break;
    }
    case App.RS: {
      setRsDaten(produktFromPayload as RsCheckoutProduktPayload, produktToAdd as RsCheckoutProdukt);
      break;
    }
    case App.RS_VERMIETUNG: {
      setRsVermietungDaten(produktFromPayload, produktToAdd as RsBaseProdukt);
      break;
    }
    case App.TKV_V2:
      setTKVV2Daten(
        produktFromPayload as TkvV2CheckoutProduktPayload,
        produktToAdd as TkvV2CheckoutProdukt
      );
      break;
    case App.TKV: {
      setTKVDaten(
        produktFromPayload as TkvCheckoutProduktPayload,
        produktToAdd as TkvCheckoutProdukt
      );
      break;
    }
    case App.UNF: {
      setUnfDaten(
        produktFromPayload as UnfCheckoutProduktPayload,
        produktToAdd as UnfCheckoutProdukt
      );
      break;
    }
    case App.GEGE: {
      setGegeDaten(
        produktFromPayload as GegeCheckoutProduktPayload,
        produktToAdd as GegeCheckoutProdukt
      );
      break;
    }
    case App.VWG:
    case App.VWG_V2:
      setVwgDaten(
        produktFromPayload as VwgCheckoutProduktPayload,
        produktToAdd as VwgCheckoutProdukt
      );
      break;
  }
}

function setVwgDaten(
  produktFromPayload: VwgCheckoutProduktPayload,
  produktToAdd: VwgCheckoutProdukt
): void {
  produktToAdd.extremwetterschutzSelbstbeteiligung =
    produktFromPayload.extremwetterschutzSelbstbeteiligung;
  produktToAdd.zuersZone = produktFromPayload.zuersZone;
}

function setVHVDaten(
  produktFromPayload: VhvCheckoutProduktPayload,
  produktToAdd: VhvCheckoutProdukt,
  previousProduktlinie?: Produktlinienname
): void {
  const previousVersicherungssumme = produktToAdd.versicherungssumme;
  const previousWertsachenValue = produktToAdd.wertsachenValue;

  produktToAdd.showAngabenZumHaushalt = produktFromPayload.showAngabenZumHaushalt;
  produktToAdd.wertsachenValue = produktFromPayload.wertsachenValue;
  produktToAdd.versicherungssumme = produktFromPayload.versicherungssumme;
  produktToAdd.showWertsachensummeQuestion = produktFromPayload.showWertsachensummeQuestion;
  if (!previousProduktlinie) {
    produktToAdd.wertsachen = initialWertsachen();
  } else {
    const wertsachenRelevantValuesChanged =
      !produktToAdd.wertsachen ||
      previousVersicherungssumme !== produktFromPayload.versicherungssumme ||
      previousWertsachenValue !== produktFromPayload.wertsachenValue ||
      (previousProduktlinie !== produktFromPayload.produktlinie);

    if (wertsachenRelevantValuesChanged) {
      produktToAdd.wertsachen = initialWertsachen();
      produktToAdd.hasWertsachen = produktFromPayload.hasWertsachen
        ? produktFromPayload.hasWertsachen
        : undefined;
    }
  }
  produktToAdd.staendigBewohnt = produktFromPayload.staendigBewohnt;
  produktToAdd.einbruchMeldeanlage = INITIAL_EMA;
  produktToAdd.tresor = INITIAL_TRESOR;
  produktToAdd.mechanischeSicherungen = INITIAL_MECHANISCHE_SICHERUNGEN;
  produktToAdd.filesToDelete = [];

  if (
    produktToAdd.stockwerk !== produktFromPayload.stockwerk &&
    produktToAdd.stockwerk === Stockwerk.OBERGESCHOSS
  ) {
    produktToAdd.mechanischeSicherungen.status = 'INCOMPLETE';
    produktToAdd.mechanischeSicherungen.kellerfenster =
      INITIAL_MECHANISCHE_SICHERUNGEN.kellerfenster;
  }
  produktToAdd.stockwerk = produktFromPayload.stockwerk;
  produktToAdd.hausart = produktFromPayload.hausart;
  if (!previousProduktlinie) {
    produktToAdd.hasWertsachen = produktFromPayload.hasWertsachen
      ? produktFromPayload.hasWertsachen
      : undefined;
  }
  produktToAdd.isTragerisikoSelected = produktFromPayload.isTragerisikoSelected;
}

function setVhvV2Daten(
  produktFromPayload: VhvV2CheckoutProduktPayload,
  produktToAdd: VhvV2CheckoutProdukt,
  previousProduktlinie?: Produktlinienname
): void {
  setVHVDaten(produktFromPayload, produktToAdd, previousProduktlinie);
  produktToAdd.extremwetterschutzSelbstbeteiligung =
    produktFromPayload.extremwetterschutzSelbstbeteiligung;
  produktToAdd.zuersZone = produktFromPayload.zuersZone;
  produktToAdd.entschaedigungsbetragRad = produktFromPayload.entschaedigungsbetragRad;
  produktToAdd.tragerisiko = produktFromPayload.tragerisiko;
}

function setRsDaten(
  produktFromPayload: RsCheckoutProduktPayload,
  produktToAdd: RsCheckoutProdukt
): void {
  produktToAdd.hasEinFahrzeugVersichert = produktFromPayload.hasEinFahrzeugVersichert;
  produktToAdd.amtlichesKennzeichen = produktFromPayload.kennzeichen ?? undefined;
  produktToAdd.wartezeitverzicht = {
    showWartezeitverzicht:
      produktFromPayload.isKombischutzSelected && isNeugeschaeft(produktFromPayload),
    wartezeitverzichtSelected: false
  } as Wartezeitverzicht;
}

function setRsVermietungDaten(
  produktFromPayload: CheckoutProdukt,
  produktToAdd: RsBaseProdukt
): void {
  produktToAdd.wartezeitverzicht = {
    showWartezeitverzicht: isNeugeschaeft(produktFromPayload),
    wartezeitverzichtSelected: false
  } as Wartezeitverzicht;
}

function setTKVDaten(
  produktFromPayload: TkvCheckoutProduktPayload,
  produktToAdd: TkvCheckoutProdukt
): void {
  produktToAdd.tierart = produktFromPayload.tierart;
  produktToAdd.inBehandlung = produktFromPayload.inBehandlung;
  produktToAdd.geburtsdatumTier = produktFromPayload.geburtsdatumTier;
}

function setTKVV2Daten(
  produktFromPayload: TkvV2CheckoutProduktPayload,
  produktToAdd: TkvV2CheckoutProdukt
): void {
  produktToAdd.animalType = produktFromPayload.animalType;
  produktToAdd.animalBirthdate = produktFromPayload.animalBirthDate;
  produktToAdd.hasIllnesses = produktFromPayload.hasIllnesses;
}

function setUnfDaten(
  produktFromPayload: UnfCheckoutProduktPayload,
  produktToAdd: UnfCheckoutProdukt
): void {
  produktToAdd.unfallVp = produktFromPayload.unfallVp;

  if (produktToAdd.unfallVp?.geburtsdatum) {
    const geburtsdatumFromISO = dayjs(produktToAdd.unfallVp.geburtsdatum, DATEFORMAT_SHORT_ISO);
    const geburtsdatumFromLOCAL = dayjs(produktToAdd.unfallVp.geburtsdatum, DATEFORMAT_LOCAL_STATE);

    if (geburtsdatumFromISO.isValid() && !geburtsdatumFromLOCAL.isValid()) {
      produktToAdd.unfallVp.geburtsdatum = geburtsdatumFromISO.format(DATEFORMAT_LOCAL_STATE);
    }
  }
}

function setGegeDaten(
  produktFromPayload: GegeCheckoutProduktPayload,
  produktToAdd: GegeCheckoutProdukt
): void {
  produktToAdd.gegenstaende = produktToAdd.gegenstaende || [];
  // Werte von neuen oder überarbeiteten Gegenständen übernehmen
  produktFromPayload.gegenstaende.forEach(gegenstand => {
    const index = produktToAdd.gegenstaende.findIndex(
      gegenstandFromCheckout => gegenstandFromCheckout.id === gegenstand.id
    );
    if (index === -1) {
      // Ein neuer Gegenstand wurde hinzugefügt
      produktToAdd.gegenstaende.push({
        id: gegenstand.id,
        kategorie: {
          id: gegenstand.kategorie.id,
          name: gegenstand.kategorie.name
        },
        kaufdatum: gegenstand.kaufdatum,
        versicherungssumme: gegenstand.versicherungssumme,
        beitrag: gegenstand.beitrag,
        modell: gegenstand.modell,
        informationen: gegenstand.informationen,
      });
    } else {
      // Der Gegenstand wurde bearbeitet
      const gegenstandFromCheckout = produktToAdd.gegenstaende[index];
      produktToAdd.gegenstaende[index] = {
        ...gegenstandFromCheckout,
        kategorie: {
          id: gegenstand.kategorie.id,
          name: gegenstand.kategorie.name
        },
        kaufdatum: gegenstand.kaufdatum,
        versicherungssumme: gegenstand.versicherungssumme,
        beitrag: gegenstand.beitrag,
        // Kategorie des Gegenstands wurde auf der Eingabeseite geändert
        modell:
          gegenstand.kategorie.name === gegenstandFromCheckout.kategorie.name
            ? gegenstandFromCheckout.modell
            : null,
        informationen:
          gegenstand.kategorie.name === gegenstandFromCheckout.kategorie.name
            ? gegenstandFromCheckout.informationen
            : null,
      };
    }
  });

  // Gelöschte Gegenstände rausfiltern
  produktToAdd.gegenstaende = produktToAdd.gegenstaende.filter(gegenstandFromCheckout =>
    produktFromPayload.gegenstaende.some(gegenstand => gegenstandFromCheckout.id === gegenstand.id)
  );

  produktToAdd.fahrradVorschaeden = produktFromPayload.fahrradVorschaeden;
}

function checkTkvSofortschutz(produkt: TkvCheckoutProdukt, state: IAppState): TkvCheckoutProdukt {
  const vorversicherung: Vorversicherung = state.vorversicherungen.find(
    vorversicherung =>
      vorversicherung.angebotsId === produkt.angebotsId && vorversicherung.appId === produkt.appId
  );
  produkt.sofortschutzActive = false;
  produkt.beginndatumChangeable = false;
  if (isTierZuJungByToday(produkt.geburtsdatumTier, DATEFORMAT_LOCAL_STATE)) {
    produkt.beginndatumChangeable = true;
  } else if (vorversicherung?.vorhanden) {
    produkt.sofortschutzActive = true;
  } else if (!vorversicherung) {
    produkt.beginndatumChangeable = true;
  }

  return produkt;
}

function checkTkvV2Sofortschutz(produkt: TkvV2CheckoutProdukt, state: IAppState): TkvV2CheckoutProdukt {
  const vorversicherung: Vorversicherung = state.vorversicherungen.find(
    vorversicherung =>
      vorversicherung.angebotsId === produkt.angebotsId && vorversicherung.appId === produkt.appId
  );
  produkt.sofortschutzActive = false;
  produkt.beginndatumChangeable = false;
  if (isTierZuJungByToday(produkt.animalBirthdate, DATEFORMAT_LOCAL_STATE)) {
    produkt.beginndatumChangeable = true;
  } else if (vorversicherung?.vorhanden) {
    produkt.sofortschutzActive = true;
  } else if (!vorversicherung) {
    produkt.beginndatumChangeable = true;
  }

  return produkt;
}

function findMatchingProdukt(sectionState: Array<Produkt>, produkt: CheckoutProdukt): Produkt {
  return sectionState.find(
    stateProdukt =>
      produkt.appId === stateProdukt.appId && produkt.angebotsId === stateProdukt.angebotsId
  );
}

export function shouldAddNewProdukt(
  sectionState: Array<Produkt>,
  produkt: CheckoutProdukt
): boolean {
  return !findMatchingProdukt(sectionState, produkt);
}

export interface HausHaltWertSachenChanged extends AngebotsId {
  hasWertsachen: boolean;
}

export function calculateVBeginnDatum(produkt: CheckoutProdukt): string {
  return produkt.wirksamkeitsbeginn
    ? produkt.wirksamkeitsbeginn
    : dayjs().add(1, 'd').format(DATEFORMAT_LOCAL_STATE);
}

function getVersichertesRisiko(produkt: CheckoutProdukt): string {
  const versichertesRisiko = produkt.isImmobilieImAusland
    ? AUSLANDS_RISIKOORT
    : produkt.versichertesRisiko;
  if (!versichertesRisiko) {
    return '';
  } else if (!produkt.geokoordinatenDetails) {
    return versichertesRisiko;
  } else {
    return versichertesRisiko + '<br>' + produkt.geokoordinatenDetails;
  }
}

function isNeugeschaeft(produktFromPayload: CheckoutProdukt): boolean {
  return !produktFromPayload.vorvertragsnummer || produktFromPayload.vorvertragsnummer.length === 0;
}
